import { useCallback, useMemo } from 'react'
import usePersistedState from './usePersistedState'
import {
  defaultColumnLayout,
  moveItem,
  normalizeColumnLayout,
} from '../utils/ticketTable'

/**
 * Preferencia de columnas de una tabla (cuáles se ven y en qué orden),
 * persistida en localStorage. Es genérico: sirve para cualquier tabla que
 * declare sus columnas como `{ key, label, defaultVisible, required? }`.
 *
 * @param {string} storageKey - Clave de localStorage (p. ej. `'myTickets.columns'`).
 * @param {Array<Object>} columns - Definición de columnas disponibles.
 * @returns {{
 *   layout: Array<Object>,
 *   visibleColumns: Array<Object>,
 *   toggleColumn: (key: string) => void,
 *   moveColumn: (from: number, to: number) => void,
 *   resetLayout: () => void
 * }}
 */
export default function useColumnLayout(storageKey, columns) {
  const [saved, setSaved] = usePersistedState(
    storageKey,
    defaultColumnLayout(columns)
  )

  /** Layout completo: definición de la columna + su preferencia `visible`. */
  const layout = useMemo(() => {
    const byKey = Object.fromEntries(columns.map((col) => [col.key, col]))
    return normalizeColumnLayout(saved, columns).map((entry) => ({
      ...byKey[entry.key],
      visible: entry.visible,
    }))
  }, [saved, columns])

  const visibleColumns = useMemo(
    () => layout.filter((col) => col.visible),
    [layout]
  )

  const toggleColumn = useCallback(
    (key) =>
      setSaved((prev) =>
        normalizeColumnLayout(prev, columns).map((entry) =>
          entry.key === key ? { ...entry, visible: !entry.visible } : entry
        )
      ),
    [columns, setSaved]
  )

  const moveColumn = useCallback(
    (from, to) =>
      setSaved((prev) =>
        moveItem(normalizeColumnLayout(prev, columns), from, to)
      ),
    [columns, setSaved]
  )

  const resetLayout = useCallback(
    () => setSaved(defaultColumnLayout(columns)),
    [columns, setSaved]
  )

  return { layout, visibleColumns, toggleColumn, moveColumn, resetLayout }
}
